/**
 * Authentication Service
 * Handles user authentication, token management, and user info
 */

import config from '../config/environment';
import { normalizeUserEmail } from '../utils/userEmail';

const TOKEN_KEY = 'aigarage_access_token';
const USER_KEY = 'aigarage_user_info';

/** Normalize display name: prefer firstName+lastName, or format email local part (e.g. sram.contractor → Sram Contractor) */
function normalizeDisplayName(user) {
  if (!user) return 'User';
  const first = user.firstName || user.first_name;
  const last = user.lastName || user.last_name;
  const fromFirstLast = [first, last].filter(Boolean).join(' ').trim();
  if (fromFirstLast) return fromFirstLast;
  const name = user.name || user.display_name || user.cn;
  if (name && typeof name === 'string') {
    const trimmed = name.trim();
    if (trimmed.includes(' ')) return trimmed;
    if (trimmed.includes('.')) {
      return trimmed.split('.').map((p) => p.charAt(0).toUpperCase() + (p.slice(1) || '').toLowerCase()).join(' ');
    }
    return trimmed.charAt(0).toUpperCase() + (trimmed.slice(1) || '').toLowerCase();
  }
  const email = user.email || user.sub || user.nameID;
  const local = email && typeof email === 'string' ? email.split('@')[0] : '';
  if (local) {
    return local.split('.').map((p) => p.charAt(0).toUpperCase() + (p.slice(1) || '').toLowerCase()).join(' ');
  }
  return 'User';
}

function userInfoFromPayload(payload) {
  if (!payload) return null;

  const firstName = payload.first_name || payload.givenName || payload.firstName;
  const lastName = payload.last_name || payload.sn || payload.lastName || payload.surname;
  const nameFromFirstLast = [firstName, lastName].filter(Boolean).join(' ').trim();
  const emailLocal = payload.email?.split('@')[0];
  const formatEmailAsName = (local) => {
    if (!local) return '';
    return local.split('.').map((p) => p.charAt(0).toUpperCase() + (p.slice(1) || '').toLowerCase()).join(' ');
  };
  const name =
    payload.name || payload.display_name || payload.cn
    || nameFromFirstLast
    || (emailLocal ? formatEmailAsName(emailLocal) : '')
    || 'User';

  return {
    email: normalizeUserEmail(payload.email || payload.sub || payload.nameID),
    name,
    groups: payload.groups || payload.group || payload.roles || [],
    authMethod: payload.auth_method || 'saml',
    firstName,
    lastName,
    department: payload.department || payload.ou,
    organization: payload.organization || payload.org,
    title: payload.title || payload.jobTitle,
    assertionId: payload.assertionId || payload.jti,
    sessionIndex: payload.sessionIndex,
    issuer: payload.iss,
    audience: payload.aud,
    employeeId: payload.employeeId || payload.employee_id,
    costCenter: payload.costCenter || payload.cost_center,
    manager: payload.manager,
    rawPayload: payload,
  };
}

class AuthService {
  constructor() {
    this._refreshInFlight = null;
  }

  /**
   * Check if we're in development mode
   */
  isDevelopmentMode() {
    return config.DEV_MODE;
  }

  /**
   * Get development user info
   */
  getDevelopmentUserInfo() {
    return {
      email: config.DEV_USER,
      name: config.DEV_USER.split('@')[0],
      groups: ['developers'],
      authMethod: 'development',
    };
  }

  hasSsoCallbackInHash() {
    const hash = window.location.hash || '';
    return hash.includes('access_token=') || hash.includes('error=');
  }

  isTokenValid(token) {
    if (!token) return false;
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) return false;
    return payload.exp > Date.now() / 1000;
  }

  /**
   * Remove stale dev-mode or expired sessions when SAML is required (test/prod Cloud Run).
   */
  purgeStaleSession() {
    if (this.isDevelopmentMode()) return;
    if (this.hasSsoCallbackInHash()) return;

    const token = this.getToken();
    const storedUserRaw = localStorage.getItem(USER_KEY);
    let stale = false;

    if (storedUserRaw) {
      try {
        const user = JSON.parse(storedUserRaw);
        if (user.authMethod === 'development') stale = true;
      } catch {
        stale = true;
      }
    }

    if (!token) {
      if (storedUserRaw) stale = true;
    } else if (!this.isTokenValid(token)) {
      stale = true;
    }

    if (stale) {
      console.log('🧹 Clearing stale auth session (expired token or dev-mode cache)');
      this.removeToken();
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    if (this.isDevelopmentMode()) {
      console.log('🔧 Development mode: bypassing authentication');
      return true;
    }

    return this.isTokenValid(this.getToken());
  }

  /**
   * Get stored access token
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Store access token
   */
  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Remove access token
   */
  removeToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  /**
   * Decode JWT token
   */
  decodeToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  userInfoFromToken(token) {
    return userInfoFromPayload(this.decodeToken(token));
  }

  /**
   * Get user info from token
   */
  getUserInfo() {
    if (this.isDevelopmentMode()) {
      console.log('🔧 Development mode: using development user info');
      return this.getDevelopmentUserInfo();
    }

    const token = this.getToken();
    const fromToken = token && this.isTokenValid(token) ? this.userInfoFromToken(token) : null;

    if (fromToken?.email) {
      const user = { ...fromToken, name: normalizeDisplayName(fromToken) };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      return user;
    }

    const storedUser = localStorage.getItem(USER_KEY);
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.email) {
          user.email = normalizeUserEmail(user.email);
        }
        user.name = normalizeDisplayName(user);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        return user;
      } catch (error) {
        console.error('Error parsing stored user info:', error);
      }
    }

    return null;
  }

  /**
   * Handle SSO callback — always processes SAML hash (ignores dev-mode bypass).
   */
  handleSSOCallback() {
    const hash = window.location.hash;

    if (hash.includes('access_token=')) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('access_token');

      if (token) {
        console.log('✅ SSO token received');
        this.setToken(token);
        localStorage.removeItem('saml_login_attempt_time');

        const userInfo = this.userInfoFromToken(token);
        if (userInfo) {
          localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
        }
        console.log('👤 User info:', userInfo);

        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        return { success: true, token, userInfo };
      }
    }

    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get('error');
      const message = params.get('message');

      console.error('❌ SSO error:', error, message);

      if (!message?.includes('timeout') && !message?.includes('504')) {
        localStorage.removeItem('saml_login_attempt_time');
      }

      window.history.replaceState(null, '', window.location.pathname + window.location.search);

      return { success: false, error, message };
    }

    return null;
  }

  /** Seconds until JWT exp, or null if unknown. */
  getTokenExpiresInSeconds() {
    const payload = this.decodeToken(this.getToken());
    if (!payload?.exp) return null;
    return payload.exp - Date.now() / 1000;
  }

  /** True when JWT should be renewed so active users are not cut off at a fixed wall-clock time. */
  needsTokenRefresh(thresholdMinutes = 10) {
    if (this.isDevelopmentMode()) return false;
    const secondsLeft = this.getTokenExpiresInSeconds();
    if (secondsLeft == null) return false;
    return secondsLeft < thresholdMinutes * 60;
  }

  async _refreshAccessToken() {
    const token = this.getToken();
    if (!token) return false;

    const base = config.API_URL.replace(/\/$/, '');
    const response = await fetch(`${base}/api/v1/auth/saml/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) return false;

    const data = await response.json();
    if (!data.access_token) return false;

    this.setToken(data.access_token);
    const userInfo = this.userInfoFromToken(data.access_token);
    if (userInfo) {
      localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
    }
    return true;
  }

  /**
   * Extend JWT while the user is active (inactivity logout is separate).
   */
  async refreshAccessTokenIfNeeded(thresholdMinutes = 10) {
    if (this.isDevelopmentMode()) return true;
    if (!this.needsTokenRefresh(thresholdMinutes)) return true;
    if (this._refreshInFlight) return this._refreshInFlight;

    this._refreshInFlight = this._refreshAccessToken()
      .catch(() => false)
      .finally(() => {
        this._refreshInFlight = null;
      });

    return this._refreshInFlight;
  }

  /**
   * Clear local session only (used by 401 handlers).
   */
  logout() {
    this.removeToken();
    try {
      localStorage.removeItem(USER_KEY);
    } catch (_) {
      /* ignore */
    }
  }

  /** Clear the local session and return the SPA to its sign-in screen. */
  signOut() {
    this.logout();

    if (this.isDevelopmentMode()) {
      window.location.href = '/ai-garage/discover';
      return;
    }

    window.dispatchEvent(new Event('aigarage:logout'));
  }

  /**
   * Get authorization header for API requests
   */
  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}

const authService = new AuthService();
export default authService;
